import Config from 'config/settings';
import { constrain } from 'utils/math';
import { Events } from 'core/events/Events';
import { CoreEvents } from 'core/events/CoreEvents';
import { ScrollerType } from 'app/data/Enums';
import { Velocity } from 'utils/motion/Velocity';
import { Vector2 } from 'app/contracts/Geometry';
import ScrollSpeedConfig from 'app/chrome/scrollbar/ScrollSpeedConfig';
import fastdom from 'fastdom';
import { ScrollbarEngineDeps } from './scrollbar.types';

/**
 * The scroll plane state and per-frame momentum / ease math for the
 * scrollbar: plane position / target / ease power, the scroll tween,
 * progress / direction / velocity and the native-vs-custom scroll write-out.
 * Scroller type / target element / bar interop is resolved in
 * ScrollbarController and arrives via the injected deps. The plane fields
 * are public because the controller's focus / snapshot / reset flows poke
 * them directly (moved verbatim from the pre-split assignment chains).
 */
export class ScrollbarEngine {
	private _deps: ScrollbarEngineDeps;

	public planeY: number = 0;
	private _oldPlaneY: number = 0;
	public planeTargetY: number = 0;
	public previousY: number = 0;
	public maxTargetScroll: number = 0;
	public easePower: number = 2;

	public direction: number = 0;
	private _tweening: boolean = false;

	public lastScroll = 0;

	//Libraries:
	private _velocity: Velocity; //Track the velocity of the page (Used to disable page interaction)
	private readonly INTERACTON_LOCK_VELOCITY: number = 0.5; //How slow should scroll plane be moving to allow interaction

	private _scrollTween;

	constructor(deps: ScrollbarEngineDeps) {
		this._deps = deps;
	}

	public get velocity(): number {
		return this._velocity.y;
	}

	public get tweening(): boolean {
		return this._tweening;
	}

	/** Create the velocity tracker (called from ScrollbarController.init at the pre-split spot) */
	public init(): void {
		this._velocity = new Velocity();
	}

	/** Get scroll as ration of max scroll */
	public getProgress(): number {
		return this.planeY / this.maxTargetScroll;
	}

	public getRealProgress(): number {
		return (-1 * this.getRealScroll()) / this.maxTargetScroll;
	}

	/** Get scroll as a 0-1 value */
	public getScroll(): number {
		return -1 * this.planeY;
	}

	public getRealScroll(): number {
		if (this._deps.getScrollerType() == ScrollerType.CUSTOM) {
			return -1 * this.planeY;
		}

		fastdom.measure(() => {
			this.lastScroll = window.scrollY;
		});

		return this.lastScroll;
	}

	public getPageTweenTime(): number {
		//Add 0.1 seconds for each 1000 pixel of scroll
		return 0.6 - this.planeY * 0.0001;
	}

	public tweenTo(
		y: number,
		time: number = -1,
		ease: any = null,
		onCompleteTween: () => void = null,
		onUpdateTween: (planeScroll) => void = null
	): void {
		if (!ease) {
			ease = Config.standardEase;
		}

		if (time === -1) {
			let pixelDiff = Math.abs(y - Math.abs(this.planeY));
			let timePerPixSecs = 0.001;
			time = 0.4 + timePerPixSecs * pixelDiff;
		}

		if (this._deps.getScrollerType() === ScrollerType.NATIVE) {
			this.planeY = window.scrollY * -1;
		}

		this._tweening = true;
		this.easePower = 1;
		this._deps.syncBarToTargetY(time, y, ease);
		let tweenOb: any = { scroll: -1 * this.planeY };

		this.killScroll();

		this._scrollTween = gsap.TweenMax.to(tweenOb, time, {
			scroll: y,
			ease: ease,
			onComplete: () => {
				this._tweening = false;
				this._scrollTween = null;

				if (onUpdateTween) {
					onUpdateTween(-1 * tweenOb.scroll);
				}

				if (onCompleteTween) {
					onCompleteTween();
				}
			},
			onUpdate: () => {
				if (this._deps.getScrollerType() === ScrollerType.CUSTOM) {
					this.planeTargetY = -1 * tweenOb.scroll;
					this.updateTargetScroll();
				} else {
					this.planeY = this.planeTargetY = -1 * tweenOb.scroll;
					window.scroll(0, tweenOb.scroll);
				}

				if (onUpdateTween) {
					onUpdateTween(-1 * tweenOb.scroll);
				}
			}
		});
	}

	public killScroll() {
		if (this._scrollTween) {
			this._scrollTween.kill();
			this._scrollTween = null;
		}
	}

	/** Get scroll */

	public scrollTo(y: number): void {
		if (this._deps.getScrollerType() === ScrollerType.CUSTOM) {
			this.planeTargetY = constrain(y, this.maxTargetScroll, 0);
			this._deps.syncBarToTargetY();
			this.updateTargetScroll();
		} else {
			let tweenOb = { scroll: Math.abs(this.planeY) };
			gsap.TweenMax.to(tweenOb, 2, {
				scroll: y,
				ease: Config.standardEase,
				onUpdate: () => {
					this.planeY = this.planeTargetY = -1 * tweenOb.scroll;

					fastdom.mutate(() => {
						window.scroll(0, tweenOb.scroll);
					});
				}
			});
		}
	}

	/** Called as the vertical drag is used */
	public verticalDrag(amount: number): void {
		this.planeTargetY = constrain(this.planeTargetY + amount, this.maxTargetScroll, 0);
		this._deps.syncBarToTargetY();
		this.easePower = 1;
	}

	/** Called after a vertical drag is release  */
	public throwPage(yVelocity: number): void {
		let scrollEffect = ScrollSpeedConfig.getSwipeEffect(yVelocity);
		this.planeTargetY = constrain(this.planeTargetY + scrollEffect.effect, this.maxTargetScroll, 0);

		this._deps.syncBarToTargetY();
		this.easePower = scrollEffect.ease;
	}

	public scroll(y: number) {
		if (this._deps.getScrollerType() === ScrollerType.NATIVE && !this._deps.isVerticalDragActive()) {
			this.planeY = this.planeTargetY = -1 * y;
			this._setDirection();
		}
	}

	/**
	 * Interactivity will be locked at a certian velocity to stop drag type interaction
	 * Most likely called by /library/scroll/InterativeArea.ts class on mouseover event
	 */
	public scrollVelocityLock(onUnlock: Function): boolean {
		//Check is plane is moving too fast to allow module interaction
		if (Math.abs(this._velocity.y) >= this.INTERACTON_LOCK_VELOCITY) {
			//Attach a callback to velcoity checker which will be triggered every update:
			this._velocity.onUpdate = (velocity: Vector2) => {
				if (velocity.y < this.INTERACTON_LOCK_VELOCITY) {
					this._velocity.onUpdate = null;
					onUnlock();
				}
			};
			return true;
		}

		//The plane isn't moving fast enough so don't lock:
		return false;
	}

	public hardScroll(y) {
		fastdom.mutate(() => {
			window.scroll(0, Math.abs(y));
		});

		this.planeTargetY = this.planeY = y;
	}

	/** Update scroll position of plane */
	public updateTargetScroll(): void {
		this._oldPlaneY = this.planeY;
		this.planeY = Math.round(
			constrain(this.planeY + (this.planeTargetY - this.planeY) / this.easePower, this.maxTargetScroll, 0)
		);

		//Only update when there is difference (as unlcear if window.scrollTo is optomised to ignore non effecting calls)
		if (this._oldPlaneY !== this.planeY) {
			this._setDirection();

			if (this._deps.getScrollerType() == ScrollerType.CUSTOM) {
				fastdom.mutate(() => {
					this._deps.getTarget().style.transform = `translate3d(0px, ${this.planeY}px, 0px)`;
				});
			} else {
				window.scroll(0, -1 * this.planeY);
			}

			Events.dispatch(CoreEvents.SCROLL, -1 * this.planeY);
		}
	}

	public resetTargetToZero($target) {
		if (this._deps.getScrollerType() == ScrollerType.CUSTOM) {
			$target.style.transform = `translate3d(0px, ${this.planeY}px, 0px)`;
		}
	}

	/** Per-frame velocity sample (delegated from ScrollbarController.tick) */
	public calculateVelocity(): void {
		this._velocity.calculate({
			x: 0,
			y: this.planeY
		});
	}

	/** Set direction */
	private _setDirection(): void {
		if (this.previousY < this.planeY) {
			this.direction = -1;
		} else if (this.previousY > this.planeY) {
			this.direction = 1;
		}

		this.previousY = this.planeY;
	}
}
